import { Box, List, ListSubheader, TextField } from "@mui/material";
import { useProviderFiles } from "containers/hooks/useData";
import { ProviderFile } from "fileParsing/types";
import { UserInteractionStatus, WranglerItem } from "fileParsing/wrangler/types";
import { useAppSelector } from "index";
import React, { useMemo, useState } from "react";
import { FileList } from "./FileList";
import { FileFilter, FileFilterPanel } from "./Filter";
import Sunburst from "./Sunburst";
import { WranglerProcessor } from "webworker/Wrangler/WranglerProcessor";

type Props = {
  exportId?: number;
  workerId?: string;
  selected?: WranglerItem;
  onChange: (selection: WranglerItem) => void;
  onConfirm: (del: boolean) => void;
};

const FileSelector: React.FC<Props> = ({ exportId, workerId, selected, onChange, onConfirm }) => {
  const STATUS_UNRESOLVED = "Unresolved";
  const STATUS_RESOLVED = "Resolved";
  const STATUS_PROCESSED = "Processed";
  const STATUS_FAILED = "Failed";
  const STATUS_IMPORTED = "No Files in Loaded Import";
  const ARR_STATUS = [STATUS_UNRESOLVED, STATUS_PROCESSED, STATUS_RESOLVED, STATUS_FAILED, STATUS_IMPORTED];

  const wranglerItems = useAppSelector((s) => s.wrangler.items);

  const [filters, setAppliedFilters] = useState<FileFilter>(new FileFilter());

  const files = useProviderFiles(exportId ?? -1);

  // Apply filters on files
  const itemsFiltersApplied = useMemo(
    () =>
      filters.apply(
        wranglerItems.filter((item) => item.exportId === exportId && item.userStatus !== UserInteractionStatus.Hidden),
        files,
      ),
    [exportId, wranglerItems, filters],
  );

  // entries for sort
  const fileLists = Object.entries(
    itemsFiltersApplied
      .filter((item) => item.files.length > 0)
      .reduce<{ [name: string]: WranglerItem[] }>((status, item) => {
        switch (true) {
          // Classify as Failed
          // case item.files.every(
          //   (f) => f.parserData?.status.algoStep === AlgoStep.Failed
          // ):
          //   return {
          //     ...status,
          //     [STATUS_FAILED]: status[STATUS_FAILED]
          //       ? [...status[STATUS_FAILED], item]
          //       : [item],
          //   };
          // Classify as Edited
          case item.userStatus === UserInteractionStatus.Edited:
            return {
              ...status,
              [STATUS_RESOLVED]: status[STATUS_RESOLVED] ? [...status[STATUS_RESOLVED], item] : [item],
            };
          // Classify as Processed
          case item.userStatus === UserInteractionStatus.Original:
            return {
              ...status,
              [STATUS_PROCESSED]: status[STATUS_PROCESSED] ? [...status[STATUS_PROCESSED], item] : [item],
            };
          // Classify as Resolved
          case item.userStatus === UserInteractionStatus.Compiled:
            return {
              ...status,
              [STATUS_RESOLVED]: status[STATUS_RESOLVED] ? [...status[STATUS_RESOLVED], item] : [item],
            };
          // Classify as Resolved
          case item.userStatus === UserInteractionStatus.Resolved:
            return {
              ...status,
              [STATUS_RESOLVED]: status[STATUS_RESOLVED] ? [...status[STATUS_RESOLVED], item] : [item],
            };
          // Classify as Unresolved
          default:
            return {
              ...status,
              [STATUS_UNRESOLVED]: status[STATUS_UNRESOLVED] ? [...status[STATUS_UNRESOLVED], item] : [item],
            };
        }
      }, {}),
  ).sort((a, b) => {
    // 1. Edited
    // 2. Unresolved
    // 3. Processed
    // 4. Failed
    return ARR_STATUS.indexOf(a[0]) - ARR_STATUS.indexOf(b[0]);
  });

  // WranglerItems with no files in current export
  const orphans = itemsFiltersApplied.filter((item) => item.files.length === 0);

  let filterDebounce: NodeJS.Timeout;

  const [minSize, maxSize, minDataPoints, maxDataPoints] = useMemo(() => {
    return wranglerItems
      .map((item) => {
        const itemFiles = WranglerProcessor.matchFiles(item, files);
        return (itemFiles as ProviderFile[]).reduce(
          (sums, file) => ({
            size: sums.size + file.uncompressedSize,
            dp: sums.dp + file.data.length,
          }),
          { size: 0, dp: 0 },
        );
      })
      .reduce(
        (minmax, item) => {
          let [minSize, maxSize, minDP, maxDP] = minmax;
          if (minSize > item.size) minSize = item.size;
          if (maxSize < item.size) maxSize = item.size;

          if (minDP > item.dp) minDP = item.dp;
          if (maxDP < item.dp) maxDP = item.dp;

          return [minSize, maxSize, minDP, maxDP];
        },
        [Number.MAX_SAFE_INTEGER, 0, Number.MAX_SAFE_INTEGER, 0],
      );
  }, [wranglerItems, files]);

  return (
    <div
      style={{
        //background: "#101010",
        borderRight: ".5px solid #505050",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      {/* Sunburst */}
      <Box sx={() => ({ margin: 0, minHeight: "330px" })}>
        <Sunburst
          files={files}
          selected={undefined} // TODO: Highlight selection
          onChoose={(path) => alert(path)}
          size={{
            height: 400,
            width: "100%",
          }}
        />
      </Box>

      {/* Freetext Search */}
      <TextField
        label="Search"
        sx={{ width: "calc(100% - 8px)", margin: "4px" }}
        variant="standard"
        onChange={(ev) => {
          clearTimeout(filterDebounce);
          filterDebounce = setTimeout(() => setAppliedFilters(filters.copyWith({ freetext: ev.target.value })), 200);
        }}
      />

      {/* Filters */}
      <FileFilterPanel
        filters={filters}
        onChange={setAppliedFilters}
        rangeSize={[minSize, maxSize]}
        rangeDataPoints={[minDataPoints, maxDataPoints]}
      />

      <List subheader={<ListSubheader />}>
        {
          // Diplay files in exports
          fileLists.map(([name, files], i) => {
            return (
              <FileList
                key={i}
                name={name}
                items={files}
                selected={selected}
                onConfirm={onConfirm}
                setSelectedItem={onChange}
                workerId={workerId}
              />
            );
          })
        }

        {
          // Display wranglerItems without files (such as from wranger imports)
          orphans.length > 0 ? (
            <FileList
              name={STATUS_IMPORTED}
              items={orphans}
              selected={selected}
              setSelectedItem={onChange}
              onConfirm={onConfirm}
              workerId={workerId}
              orphan={true}
            />
          ) : undefined
        }
      </List>
    </div>
  );
};

export default FileSelector;
